'use client';

import { useState, useLayoutEffect, RefObject } from 'react';

type ScrollDirection = 'horizontal' | 'vertical';

export const useScrollShadow = (
  scrollRef: RefObject<HTMLDivElement | null>,
  direction: ScrollDirection = 'horizontal',
) => {
  const [showStart, setShowStart] = useState(false);
  const [showEnd, setShowEnd] = useState(false);

  useLayoutEffect(() => {
    const element = scrollRef.current;
    if (!element) return;

    const checkScrollable = () => {
      if (direction === 'horizontal') {
        const { clientWidth, scrollLeft, scrollWidth } = element;
        const isScrollable = scrollWidth > clientWidth;
        const isScrolledToStart = scrollLeft <= 1;
        const isScrolledToEnd = scrollWidth - scrollLeft - clientWidth <= 1;

        setShowStart(isScrollable && !isScrolledToStart);
        setShowEnd(isScrollable && !isScrolledToEnd);
      } else {
        const { clientHeight, scrollHeight, scrollTop } = element;
        const isScrollable = scrollHeight > clientHeight;
        const isScrolledToStart = scrollTop <= 1;
        const isScrolledToEnd = scrollHeight - scrollTop - clientHeight <= 1;

        setShowStart(isScrollable && !isScrolledToStart);
        setShowEnd(isScrollable && !isScrolledToEnd);
      }
    };

    checkScrollable();
    element.addEventListener('scroll', checkScrollable);
    window.addEventListener('resize', checkScrollable);

    const resizeObserver = new ResizeObserver(checkScrollable);
    resizeObserver.observe(element);

    return () => {
      element.removeEventListener('scroll', checkScrollable);
      window.removeEventListener('resize', checkScrollable);
      resizeObserver.disconnect();
    };
  }, [scrollRef, direction]);

  return { showEnd, showStart };
};
